import {
  SelectChangeEvent,
  Grid,
  Autocomplete,
  Fade,
  Typography,
  Divider,
} from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { initial_tool_state } from "../../utils/data";
import { ProductProps } from "../../interfaces/interfaces";
import {
  StyledTextField,
  StyledFormButton,
  StyledFormContainer,
  StyledFormSection,
} from "../../utils/formStyles";

interface Props {
  productValues?: ProductProps;
  onEdit?: (values: ProductProps) => void;
  onCreatetool?: (values: ProductProps) => void;
  onRemoveInnerTool?: () => void;
  onCancel: () => void;
  disabled?: boolean;
}

const diameterOptions = [
  '2-3/8" EUE box',
  '2-3/8" EUE pin',
  '2-7/8" EUE box',
  '2-7/8" EUE pin',
  '3-1/2" EUE box',
  '3-1/2" EUE pin',
  '4-1/2" EUE box',
  '4-1/2" EUE pin',
  "-",
];

export const ProductForm = ({
  productValues,
  onEdit,
  onCreatetool,
  onRemoveInnerTool,
  onCancel,
  disabled,
}: Props) => {
  const [values, setValues] = useState(productValues || initial_tool_state);
  const [toSave, setToSave] = useState(false);

  useEffect(() => {
    if (values.name !== "" && values.length !== 0 && values.quantity !== 0)
      setToSave(true);
  }, [values]);

  const onChangeValue = (
    event:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<number>,
  ) => {
    setValues((prevState) => {
      return {
        ...prevState,
        [event.target.name]: event.target.value,
      };
    });
  };

  return (
    <Fade in timeout={600}>
      <StyledFormContainer>
        <StyledFormSection>
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: "primary.main", fontWeight: 600, mb: 1 }}
          >
            OSI Tool
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            {onCreatetool && (
              <Grid item xs={12}>
                <StyledTextField
                  type="text"
                  label="Part Number"
                  variant="outlined"
                  name="partNumber"
                  value={values.partNumber}
                  onChange={onChangeValue}
                  fullWidth
                />
              </Grid>
            )}
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="text"
                label="Description"
                variant="outlined"
                name="name"
                value={values.name}
                disabled={disabled}
                onChange={onChangeValue}
                fullWidth
                required
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <Autocomplete
                renderOption={(props: any, option: string) => (
                  <li {...props} key={option}>
                    {option}
                  </li>
                )}
                value={values.topThreadConnection}
                onChange={(event: any, newValue: string | null) =>
                  newValue &&
                  setValues((prev) => {
                    return { ...prev, topThreadConnection: newValue };
                  })
                }
                options={diameterOptions}
                disablePortal
                renderInput={(params: any) => (
                  <StyledTextField {...params} label="Top Thread Connection" />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: 1,
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(90, 100, 119, 0.15)",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <Autocomplete
                renderOption={(props: any, option: string) => (
                  <li {...props} key={option}>
                    {option}
                  </li>
                )}
                value={values.bottomThreadConnection}
                onChange={(event: any, newValue: string | null) =>
                  newValue &&
                  setValues((prev) => {
                    return { ...prev, bottomThreadConnection: newValue };
                  })
                }
                options={diameterOptions}
                disablePortal
                renderInput={(params: any) => (
                  <StyledTextField
                    {...params}
                    label="Bottom Thread Connection"
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: 1,
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(90, 100, 119, 0.15)",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="text"
                label="Max OD (in)"
                name="maxOD"
                variant="outlined"
                value={values.maxOD}
                disabled={disabled}
                onChange={onChangeValue}
                fullWidth
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="text"
                label="Body OD (in)"
                name="bodyOD"
                variant="outlined"
                value={values.bodyOD}
                disabled={disabled}
                onChange={onChangeValue}
                fullWidth
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="number"
                label="Length (ft)"
                name="length"
                variant="outlined"
                value={values.length}
                disabled={disabled}
                onChange={onChangeValue}
                fullWidth
                required
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="number"
                label="Weight (Lb)"
                name="weight"
                variant="outlined"
                value={values.weight}
                disabled={disabled}
                onChange={onChangeValue}
                fullWidth
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="number"
                label="Quantity"
                name="quantity"
                variant="outlined"
                value={values.quantity}
                disabled={disabled}
                onChange={onChangeValue}
                fullWidth
                required
              />
            </Grid>
          </Grid>
        </StyledFormSection>

        <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item>
            <StyledFormButton
              onClick={onCancel}
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              Cancel
            </StyledFormButton>
          </Grid>
          {onEdit && (
            <Grid item>
              <StyledFormButton
                onClick={() => onEdit(values)}
                variant="contained"
                disabled={!toSave || disabled}
                sx={{ minWidth: 120 }}
              >
                Edit Tool
              </StyledFormButton>
            </Grid>
          )}
          {onCreatetool && (
            <Grid item>
              <StyledFormButton
                onClick={() => onCreatetool(values)}
                variant="contained"
                disabled={!toSave || disabled}
                sx={{ minWidth: 120 }}
              >
                Add Tool
              </StyledFormButton>
            </Grid>
          )}
          {disabled && onRemoveInnerTool && (
            <Grid item>
              <StyledFormButton
                onClick={() => onRemoveInnerTool()}
                variant="outlined"
                color="error"
                sx={{ minWidth: 120 }}
              >
                Remove
              </StyledFormButton>
            </Grid>
          )}
        </Grid>
      </StyledFormContainer>
    </Fade>
  );
};
